import React, { useState } from 'react';
import { ArrowLeftIcon, UploadIcon, StarIcon, MoreHorizontalIcon } from 'lucide-react';
interface RoomImage {
  id: string;
  url: string;
  caption: string;
  uploadedBy: string;
  uploadDate: string;
  isActive: boolean;
  isFeatured?: boolean;
}
interface RoomAssetsProps {
  roomId: string;
  roomName: string;
  images: RoomImage[];
  onBack: () => void;
  onUpload: (files: FileList) => void;
  onUpdateImage: (imageId: string, updates: Partial<RoomImage>) => void;
  onDeleteImage: (imageId: string) => void;
}
export function RoomAssets({
  roomId,
  roomName,
  images,
  onBack,
  onUpload,
  onUpdateImage,
  onDeleteImage
}: RoomAssetsProps) {
  const [activeTab, setActiveTab] = useState<'info' | 'media'>('media');
  const [selectedImages, setSelectedImages] = useState<string[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      onUpload(e.dataTransfer.files);
    }
  };
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      onUpload(e.target.files);
    }
  };
  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelectedImages(images.map(img => img.id));
    } else {
      setSelectedImages([]);
    }
  };
  const handleSelectImage = (imageId: string, checked: boolean) => {
    if (checked) {
      setSelectedImages([...selectedImages, imageId]);
    } else {
      setSelectedImages(selectedImages.filter(id => id !== imageId));
    }
  };
  return <div className="space-y-6" data-id="element-3136">
      <div className="flex items-center" data-id="element-3137">
        <button onClick={onBack} className="flex items-center text-gray-600 hover:text-gray-900" data-id="element-3138">
          <ArrowLeftIcon size={18} className="mr-1" data-id="element-3139" />
          <span data-id="element-3140">Room Assets</span>
        </button>
      </div>
      <div className="bg-white rounded-lg border border-gray-200" data-id="element-3141">
        {/* Tabs */}
        <div className="border-b border-gray-200" data-id="element-3142">
          <nav className="flex" data-id="element-3143">
            <button className={`px-6 py-4 text-sm font-medium ${activeTab === 'info' ? 'border-b-2 border-red-500 text-red-600' : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} onClick={() => setActiveTab('info')} data-id="element-3144">
              Room information
            </button>
            <button className={`px-6 py-4 text-sm font-medium ${activeTab === 'media' ? 'border-b-2 border-red-500 text-red-600' : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} onClick={() => setActiveTab('media')} data-id="element-3145">
              Media assets
            </button>
          </nav>
        </div>
        {/* Media Assets Content */}
        <div className="p-6" data-id="element-3146">
          {/* File Upload Area */}
          <div className={`border-2 border-dashed rounded-lg p-8 mb-6 flex flex-col items-center justify-center ${dragActive ? 'border-red-500 bg-red-50' : 'border-gray-300'}`} onDragEnter={handleDrag} onDragOver={handleDrag} onDragLeave={handleDrag} onDrop={handleDrop} data-id="element-3147">
            <div className="p-3 bg-red-100 rounded-full mb-4" data-id="element-3148">
              <UploadIcon size={24} className="text-red-500" data-id="element-3149" />
            </div>
            <p className="text-gray-600 mb-1" data-id="element-3150">Drag files here</p>
            <p className="text-xs text-gray-500 mb-4" data-id="element-3151">
              Supported formats: PNG, JPG, JPEG
            </p>
            <p className="text-xs text-gray-500 mb-4" data-id="element-3152">Max file size: 10MB</p>
            <label className="cursor-pointer" data-id="element-3153">
              <input type="file" accept="image/png,image/jpeg,image/jpg" className="hidden" onChange={handleFileChange} multiple data-id="element-3154" />
              <span className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700" data-id="element-3155">
                Upload
              </span>
            </label>
          </div>
          {/* Images Table */}
          <div className="overflow-x-auto" data-id="element-3156">
            <table className="min-w-full divide-y divide-gray-200" data-id="element-3157">
              <thead data-id="element-3158">
                <tr data-id="element-3159">
                  <th className="w-12 py-3 pl-4" data-id="element-3160">
                    <input type="checkbox" checked={selectedImages.length === images.length && images.length > 0} onChange={handleSelectAll} className="h-4 w-4 text-red-600 border-gray-300 rounded" data-id="element-3161" />
                  </th>
                  <th className="py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3162">
                    Room image
                  </th>
                  <th className="py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3163">
                    Caption
                  </th>
                  <th className="py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3164">
                    Uploaded by
                  </th>
                  <th className="py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3165">
                    Uploaded
                  </th>
                  <th className="py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3166">
                    Visibility
                  </th>
                  <th className="py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3167">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-3168">
                {images.map(image => <tr key={image.id} data-id="element-3169">
                    <td className="py-4 pl-4" data-id="element-3170">
                      <input type="checkbox" checked={selectedImages.includes(image.id)} onChange={e => handleSelectImage(image.id, e.target.checked)} className="h-4 w-4 text-red-600 border-gray-300 rounded" data-id="element-3171" />
                    </td>
                    <td className="py-4" data-id="element-3172">
                      <div className="w-24 h-16 bg-gray-100 rounded overflow-hidden" data-id="element-3173">
                        <img src={image.url} alt={image.caption || 'Room image'} className="w-full h-full object-cover" data-id="element-3174" />
                      </div>
                    </td>
                    <td className="py-4" data-id="element-3175">
                      <div className="flex items-center" data-id="element-3176">
                        {image.isFeatured && <StarIcon size={16} className="text-yellow-400 mr-1" data-id="element-3177" />}
                        <span data-id="element-3178">{image.caption || 'Untitled'}</span>
                      </div>
                    </td>
                    <td className="py-4 text-sm text-gray-500" data-id="element-3179">
                      {image.uploadedBy}
                    </td>
                    <td className="py-4 text-sm text-gray-500" data-id="element-3180">
                      {image.uploadDate}
                    </td>
                    <td className="py-4" data-id="element-3181">
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${image.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-3182">
                        {image.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="py-4" data-id="element-3183">
                      <button className="text-gray-400 hover:text-gray-500" data-id="element-3184">
                        <MoreHorizontalIcon size={16} data-id="element-3185" />
                      </button>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>;
}